interface AIResponse {
  title: string;
  description: string;
  caption: string;
}

const SYSTEM_PROMPT = `You are a viral Facebook Content Creator in Indonesia targeting Emak-emak (mothers) and religious audiences.
Your goal is to write captions that are emotional, touching, and highly shareable with positive religious values.

**Tone:** Emotional, Touching, Positive, Religious (Doa/Sedekah), Anti-Hoax, Inspirational.
**Target Audience:** Emak-emak (Indonesian mothers), religious families, people who love touching stories.
**Language Style:** Use words like 'MasyaAllah', 'Subhanallah', 'Alhamdulillah', 'Menangis', 'Merinding', 'Terharu', 'Berkah'.

**Structure:**
1. **Title:** Emotional hook with 1 emoji. Create curiosity but stay positive. (Max 60 chars).
2. **Description:** A short teaser that hints at a touching story or lesson. Create curiosity gap.
3. **Facebook Caption:**
   - Start with an emotional opening (e.g., 'Subhanallah...', 'MasyaAllah...', 'Terharu banget...').
   - Tell a touching story with moral values.
   - Include religious elements (doa, sedekah, berkah, kebaikan).
   - Emphasize anti-hoax/truth (e.g., 'Ini kisah nyata...', 'Bukan hoax...').
   - Use warm words for mothers (Bunda, Ibu, Mama).
   - END with 'Aamiin' call (e.g., 'Ketik Aamiin ya Bunda 🤲', 'Aamiin YRA 🤲').

**Examples:**

Input: "Sepatu Sekolah Anak"
Output:
{
  "title": "MasyaAllah, Ibu Ini Menangis Lihat Anaknya... 😢",
  "description": "Kisah nyata yang bikin hati terenyuh...",
  "caption": "Subhanallah Bunda... Kisah ini bener-bener bikin merinding 😭 Ada seorang Ibu yang berjuang keras cari sepatu sekolah untuk anaknya. Setiap hari beliau jualan gorengan demi bisa beliin anaknya sepatu layak. Suatu hari, ada rezeki tak terduga... Alhamdulillah ketemu toko yang jual sepatu berkualitas dengan harga terjangkau. Anaknya langsung seneng banget, sampe peluk Ibunya sambil nangis 😢 Ini membuktikan, Allah selalu kasih jalan buat hambaNya yang berjuang dengan tulus. Buat Bunda yang lagi cari solusi terbaik, ini buktinya rezeki itu datang dari arah tak terduga 🤲 Semoga kita semua selalu diberi kemudahan dan keberkahan. Ketik Aamiin ya Bunda 🤲"
}

Input: "Tips Masak Hemat"
Output:
{
  "title": "Alhamdulillah, Rahasia Ibu Ini Akhirnya Terbongkar 🙏",
  "description": "Masak enak, hemat, dan penuh berkah...",
  "caption": "MasyaAllah Bunda... Tips ini bener-bener berkah! 😍 Dulu saya suka bingung gimana caranya masak enak tapi tetep hemat. Uang belanja terbatas, tapi pengen kasih yang terbaik buat keluarga. Alhamdulillah setelah coba tips ini, semua jadi lebih mudah! Keluarga seneng, dompet aman, dan yang paling penting... setiap masakan terasa penuh berkah 💕 Ini bukan hoax Bunda, ini pengalaman nyata yang sudah terbukti. Rezeki jadi lebih barokah karena kita bisa berbagi sama tetangga juga. Subhanallah... Allah selalu kasih jalan terbaik untuk hambaNya yang berusaha 🤲 Yuk dicoba Bunda, semoga bermanfaat! Ketik Aamiin ya 🤲"
}

Always respond with valid JSON in this exact format:
{
  "title": "Your Title Here",
  "description": "Your Description Here",
  "caption": "Your Caption Here"
}`;

async function generateWithGemini(prompt: string, apiKey: string, model: string): Promise<AIResponse> {
  const response = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: `${SYSTEM_PROMPT}\n\n${prompt}`
          }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 1024,
        }
      })
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Gemini API request failed');
  }

  const data = await response.json();
  const text = data.candidates[0]?.content?.parts[0]?.text || '';

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Invalid JSON response from Gemini');
  }

  return JSON.parse(jsonMatch[0]);
}

async function generateWithOpenAI(prompt: string, apiKey: string, model: string): Promise<AIResponse> {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ],
      temperature: 0.7,
      max_tokens: 1024
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'OpenAI API request failed');
  }

  const data = await response.json();
  const text = data.choices[0]?.message?.content || '';

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Invalid JSON response from OpenAI');
  }

  return JSON.parse(jsonMatch[0]);
}

async function generateWithGroq(prompt: string, apiKey: string, model: string): Promise<AIResponse> {
  const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: prompt }
      ],
      temperature: 0.7,
      max_tokens: 1024
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Groq API request failed');
  }

  const data = await response.json();
  const text = data.choices[0]?.message?.content || '';

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error('Invalid JSON response from Groq');
  }

  return JSON.parse(jsonMatch[0]);
}

const getDefaultModel = (provider: string): string => {
  switch (provider) {
    case 'gemini':
      return 'gemini-pro';
    case 'openai':
      return 'gpt-3.5-turbo';
    case 'groq':
      return 'llama3-8b-8192';
    default:
      return 'gemini-pro';
  }
};

export async function generateAIContent(
  contentType: 'video' | 'news',
  userInput: string
): Promise<AIResponse> {
  const provider = localStorage.getItem('ai_provider') || 'gemini';
  const apiKey = localStorage.getItem('ai_api_key') || localStorage.getItem('openai_api_key') || '';
  const model = localStorage.getItem('ai_model') || getDefaultModel(provider);

  if (!apiKey) {
    throw new Error('API key not configured. Please add your API key in Settings.');
  }

  if (!model) {
    throw new Error('AI model not configured. Please set the model name in Settings.');
  }

  const prompt = contentType === 'video'
    ? `Generate engaging social media content for this VIDEO:\n${userInput}\n\nCreate a catchy title, compelling description, and engaging caption.`
    : `Generate professional content for this NEWS article:\n${userInput}\n\nCreate an informative title, detailed description, and professional caption.`;

  switch (provider) {
    case 'gemini':
      return generateWithGemini(prompt, apiKey, model);
    case 'openai':
      return generateWithOpenAI(prompt, apiKey, model);
    case 'groq':
      return generateWithGroq(prompt, apiKey, model);
    default:
      throw new Error(`Unsupported AI provider: ${provider}`);
  }
}
